import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';

import { Store } from '@ngrx/store';

import { forkJoin } from 'rxjs';

import { filter, take } from 'rxjs/operators';

import {
  createDefaultFlockWeekChartFilters,
  Flock,
  FlockModuleState,
  FlockWeekChartFilters,
  PorphyrioCategory,
  PorphyrioEggMarketingStandardFound,
} from '../../models/flock.model';

import { upsertFlockWeekChartFiltersRequest } from '../../store/actions/flock.actions';
import {
  getFlocksForAllLocations,
  getFlockWeekChartFilters,
  getPorphyrioCategories,
} from '../../store/selectors/flock.selectors';

@Component({
  selector: 'app-flock-overview-week-chart-settings',
  templateUrl: './flock-overview-week-chart-settings.component.html',
  styleUrls: ['./flock-overview-week-chart-settings.component.scss'],
  standalone: false,
})
export class FlockOverviewWeekChartSettingsComponent extends AbstractDialog implements OnInit {
  private readonly porphyrioCategoriesOrder: string[] = ['overall', 'white', 'brown'];

  porphyrioCategories: PorphyrioCategory[];
  porphyrioEggMarketingStandardsFound: PorphyrioEggMarketingStandardFound[];
  porphyrioBreeds: string[];
  flockWeekChartFilters: FlockWeekChartFilters = createDefaultFlockWeekChartFilters();
  flocksForAllLocations: Flock[];

  porphyrioCategories$ = this.store.select(getPorphyrioCategories);
  flockWeekChartFilters$ = this.store.select(getFlockWeekChartFilters);
  flocksForAllLocations$ = this.store.select(getFlocksForAllLocations);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AbstractDialogData,
    protected mdDialogRef: MatDialogRef<
      FlockOverviewWeekChartSettingsComponent,
      AbstractDialogResultData
    >,
    private store: Store<FlockModuleState>,
  ) {
    super();
  }

  ngOnInit(): void {
    forkJoin([
      this.porphyrioCategories$.pipe(
        filter((porphyrioCategories) => !!porphyrioCategories),
        take(1),
      ),
      this.flockWeekChartFilters$.pipe(
        filter((flockWeekChartFilters) => !!flockWeekChartFilters),
        take(1),
      ),
      this.flocksForAllLocations$.pipe(
        filter((flocksForAllLocations) => flocksForAllLocations.length > 0),
        take(1),
      ),
    ]).subscribe(([porphyrioCategories, flockWeekChartFilters, flocksForAllLocations]) => {
      this.porphyrioCategories = this.sortPorphyrioCategories(porphyrioCategories.categories);
      this.flockWeekChartFilters = flockWeekChartFilters;
      this.flocksForAllLocations = flocksForAllLocations;
      this.setStandardsAndBreeds();
    });
  }

  private sortPorphyrioCategories(porphyrioCategories: PorphyrioCategory[]): PorphyrioCategory[] {
    return this.porphyrioCategoriesOrder.reduce((accumulator, item) => {
      const foundCategory = porphyrioCategories.find(
        (porphyrioCategory) => porphyrioCategory.category === item,
      );

      return foundCategory ? [...accumulator, foundCategory] : accumulator;
    }, []);
  }

  private sortBreeds(breeds: string[]): string[] {
    const sortedBreeds: string[] = breeds.slice().sort((a, b) => a.localeCompare(b));
    const overallBreedIndex = sortedBreeds.indexOf('Overall');

    if (overallBreedIndex >= 0) {
      sortedBreeds.unshift(sortedBreeds.splice(overallBreedIndex, 1)[0]);
    }

    return sortedBreeds;
  }

  private retrieveEggMarketingStandardsFound(
    porphyrioCategories: PorphyrioCategory[],
    flockWeekChartFilters: FlockWeekChartFilters,
  ): PorphyrioEggMarketingStandardFound[] {
    return (
      porphyrioCategories
        .find(
          (porphyrioCategory) =>
            porphyrioCategory.category === flockWeekChartFilters.breedCategoryName,
        )
        ?.eggMarketingStandardsFound?.slice()
        .sort((a, b) => a.standard - b.standard) || []
    );
  }

  private retrieveBreeds(
    eggMarketingStandardsFound: PorphyrioEggMarketingStandardFound[],
    flockWeekChartFilters: FlockWeekChartFilters,
  ): string[] {
    return this.sortBreeds(
      eggMarketingStandardsFound.find(
        (eggMarketingStandardFound) =>
          eggMarketingStandardFound.standard === flockWeekChartFilters.eggMarketingStandard,
      )?.breeds || [],
    );
  }

  changeBreedCategoryNameFilter(newBreedCategoryName: string): void {
    this.flockWeekChartFilters = {
      ...this.flockWeekChartFilters,
      breedCategoryName: newBreedCategoryName,
    };
    this.setStandardsAndBreeds();
    this.checkBreedNameFilter();
  }

  changeEggMarketingStandardFilter(newEggMarketingStandard: number): void {
    this.flockWeekChartFilters = {
      ...this.flockWeekChartFilters,
      eggMarketingStandard: newEggMarketingStandard,
    };
    this.setStandardsAndBreeds();
    this.checkBreedNameFilter();
  }

  changeBreedNameFilter(newBreedName: string): void {
    this.flockWeekChartFilters = {
      ...this.flockWeekChartFilters,
      breedName: newBreedName,
    };
  }

  toggleShowValue(variableName: string, oldValue: boolean): void {
    this.flockWeekChartFilters = {
      ...this.flockWeekChartFilters,
      [variableName]: !oldValue,
    };

    if (variableName === 'showOtherFlock') {
      if (this.flocksForAllLocations?.length > 0 && !oldValue) {
        this.changeSelectedOtherFlockId(this.flocksForAllLocations[0].id);
        return;
      }

      this.changeSelectedOtherFlockId(null);
    }
  }

  changeSelectedOtherFlockId(newOtherFlockId: number): void {
    this.flockWeekChartFilters = {
      ...this.flockWeekChartFilters,
      selectedOtherFlockId: newOtherFlockId,
    };
  }

  private setStandardsAndBreeds(): void {
    this.porphyrioEggMarketingStandardsFound = this.retrieveEggMarketingStandardsFound(
      this.porphyrioCategories,
      this.flockWeekChartFilters,
    );
    this.porphyrioBreeds = this.retrieveBreeds(
      this.porphyrioEggMarketingStandardsFound,
      this.flockWeekChartFilters,
    );
  }

  private checkBreedNameFilter(): void {
    if (!this.porphyrioBreeds.includes(this.flockWeekChartFilters.breedName)) {
      this.flockWeekChartFilters.breedName = '';
    }
  }

  get hasPorphyrioCategories(): boolean {
    return !!this.porphyrioCategories?.length;
  }

  save(): void {
    this.store.dispatch(
      upsertFlockWeekChartFiltersRequest({
        flockWeekChartFilters: this.flockWeekChartFilters,
      }),
    );

    this.confirm();
  }
}
